'use client';
import React, { useState, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { Button } from '@components/ui/button';
import { PricingCard } from '@/components/ui/PricingCard';
import { sendGTMEvent } from '@components/GoogleTagManager';
import { useTrackClientEvent } from '@analytics/trackClientEvent';
import { slugify } from '@acme/lib';
import { api } from '@/trpc/react';

interface Product {
  productName: string;
  priceId: string;
  price?: number;
  features?: string[];
  pricePerPhoto?: string;
  pricePerPhotoAmount?: number;
}

interface PricingBundleSelectorProps {
  products: Product[];
  discountCode?: string;
  discountPercentage?: number;
  showCents?: boolean;
  className?: string;
  returnUrl?: string;
  cancelUrl?: string;
  variant?: 'select' | 'direct';
  buttonText?: string;
  redirectToTrial?: boolean;
  loginRedirectUrl?: string;
}

const calculateDiscountedPrice = (price: number, discountPercentage: number) => {
  return price * (1 - discountPercentage / 100);
};

export function PricingBundleSelector({
  products,
  discountCode,
  discountPercentage = 0,
  showCents = false,
  className = '',
  returnUrl = '/train?hideContinueModal=true&step=skinTone',
  cancelUrl = '/onboarding/select-bundle?cancelCheckout=true',
  variant = 'select',
  buttonText,
  redirectToTrial = false,
  loginRedirectUrl = '/onboarding/select-bundle'
}: PricingBundleSelectorProps) {
  const router = useRouter();
  const { trackEvent } = useTrackClientEvent();
  const { status } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [processingPriceId, setProcessingPriceId] = useState<string | null>(null);

  // Query to check if the user can use a first-time promotion
  const { data: promotionData } = api.user.canUseFirstTimePromotion.useQuery(undefined, {
    enabled: status === 'authenticated'
  });

  const isCouponApplied = discountCode !== undefined;

  // Find Pro Bundle's priceId and set it as default
  const defaultTier = useMemo(() => {
    return products?.find(t => t.productName === 'Pro Bundle')?.priceId || null;
  }, [products]);

  const [selectedTier, setSelectedTier] = useState<string | null>(defaultTier);

  const selectedProduct = products?.find(t => t.priceId === selectedTier);

  const navigateToCheckout = async (priceId: string) => {
    if (status === 'unauthenticated') {
      router.push(`/login?redirect=${loginRedirectUrl}`);
      return;
    }

    const tier = products?.find(t => t.priceId === priceId);
    if (!tier || typeof tier.price !== 'number') return;

    setIsLoading(true);
    if (variant === 'direct') {
      setProcessingPriceId(priceId);
    }

    try {
      sendGTMEvent({
        event: 'select_tier',
        tier: tier.productName,
        price: isCouponApplied ? calculateDiscountedPrice(tier.price, discountPercentage) : tier.price
      });

      trackEvent('initiateCheckout', {
        value: tier.priceId
      });
      sendGTMEvent({
        event: 'initiateCheckout',
        value: tier.priceId,
        discount_applied: isCouponApplied
      });

      // Check if user can use first-time promotions
      const canUseFirstTimePromotion = promotionData?.canUsePromotion ?? false;

      // Determine redirect based on redirectToTrial prop, discount code, and promotion eligibility
      if (!redirectToTrial || discountCode || !canUseFirstTimePromotion) {
        // Direct checkout path - construct URL string
        const params = new URLSearchParams({
          quantity: '1',
          returnUrl,
          cancelUrl,
          ...(isCouponApplied && discountCode && { discountCode })
        });
        router.push(`/payment/initial-purchase/checkout/${slugify(tier.productName)}?${params.toString()}`);
      } else {
        // Trial path
        router.push('/drive/trial?tier=' + slugify(encodeURIComponent(tier.productName)));
      }
    } catch (error) {
      console.error('Error navigating to checkout:', error);
    } finally {
      setIsLoading(false);
      setProcessingPriceId(null);
    }
  };

  const handleSelectTier = async () => {
    if (!selectedTier) return;
    await navigateToCheckout(selectedTier);
  };

  const handleBuyNow = async (priceId: string) => {
    await navigateToCheckout(priceId);
  };

  const renderButton = () => (
    <Button
      size="lg"
      variant="accent"
      disabled={!selectedTier || isLoading}
      onClick={handleSelectTier}
      className="w-full"
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : selectedProduct ? (
        <>Continue with {selectedProduct.productName}</>
      ) : (
        'Select a bundle to continue'
      )}
    </Button>
  );

  return (
    <div className={className}>
      <div className="grid lg:grid-cols-3 gap-4">
        {products?.map(tier => {
          if (!tier.price) return null;

          return (
            <PricingCard
              key={tier.priceId}
              productName={tier.productName}
              priceId={tier.priceId}
              price={isCouponApplied ? calculateDiscountedPrice(tier.price, discountPercentage) : tier.price}
              originalPrice={tier.price}
              features={tier.features || []}
              isSelected={selectedTier === tier.priceId}
              onSelect={variant === 'select' ? setSelectedTier : undefined}
              pricePerPhoto={tier.pricePerPhoto}
              pricePerPhotoAmount={
                isCouponApplied && tier.pricePerPhotoAmount
                  ? (Number(tier.pricePerPhotoAmount) * (1 - discountPercentage / 100)).toFixed(2)
                  : String(tier.pricePerPhotoAmount || '')
              }
              isDiscounted={isCouponApplied}
              discountPercentage={isCouponApplied ? discountPercentage : undefined}
              showCents={showCents}
              variant={variant}
              onBuy={variant === 'direct' ? handleBuyNow : undefined}
              isLoading={processingPriceId === tier.priceId}
              buttonText={buttonText || `Buy ${tier.productName}`}
            />
          );
        })}
      </div>

      {/* Only show these buttons in 'select' variant */}
      {variant === 'select' && (
        <>
          {/* Desktop button - visible only on large screens */}
          <div className="hidden lg:flex justify-center flex-col items-center mt-8 mb-4">
            <div className="w-full max-w-md">{renderButton()}</div>
          </div>

          {/* Mobile button - fixed at bottom, visible only on small screens */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background/90 backdrop-blur-sm border-t p-4 z-10">
            {renderButton()}
          </div>
        </>
      )}
    </div>
  );
}
